import { useCart } from "./CartContext";
import { useNavigate } from "react-router";
import { CheckCircle } from "lucide-react";
import { useLanguage } from "./LanguageContext";
import { useTheme } from "./ThemeInjector";
import { ISO_COUNTRIES } from "../data/isoCountries";

export function ConfirmationPage() {
  const { lastOrder } = useCart();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const theme = useTheme();

  return (
    <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-8"
        style={{ backgroundColor: `${theme.primary}1a` }}
      >
        <CheckCircle size={32} style={{ color: theme.primary }} />
      </div>

      <h1
        className="tracking-[0.1em] mb-4"
        style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, color: theme.fg }}
      >
        {t("confirmation.title")}
      </h1>

      <p
        className="leading-[1.8] mb-4 max-w-md mx-auto"
        style={{ fontFamily: theme.fontBody, fontSize: "16px", fontWeight: 300, color: theme.muted }}
      >
        {t("confirmation.message")}
      </p>

      {lastOrder && (
        <div className="rounded-xl p-6 shadow-[0_8px_30px_rgba(40,32,20,0.06)] text-left mt-10 mb-10" style={{ backgroundColor: theme.card }}>
          <p
            className="tracking-[0.3em] uppercase mb-5"
            style={{ fontFamily: theme.fontBody, fontSize: "11px", color: theme.accent }}
          >
            {t("confirmation.summary")}
          </p>
          <div className="space-y-3 mb-5">
            {lastOrder.items.map((item) => (
              <div key={item.id} className="flex justify-between">
                <span style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.fg }}>
                  {item.quantity} &times; {item.name}
                </span>
                <span style={{ fontFamily: theme.fontBody, fontSize: "14px", color: theme.fg }}>
                  &euro;{item.price * item.quantity}
                </span>
              </div>
            ))}
          </div>
          <div className="pt-4 flex justify-between" style={{ borderTop: `1px solid ${theme.border}` }}>
            <span
              className="tracking-[0.15em] uppercase"
              style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.muted }}
            >
              {t("cart.total")}
            </span>
            <span style={{ fontFamily: theme.fontHeading, fontSize: "22px", fontWeight: 500, color: theme.fg }}>
              &euro;{lastOrder.total}
            </span>
          </div>
          <div className="mt-5 pt-4 space-y-1" style={{ borderTop: `1px solid ${theme.border}` }}>
            <p style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 300, color: `${theme.muted}99` }}>
              {lastOrder.name} &middot; {lastOrder.email}
            </p>
            <p style={{ fontFamily: theme.fontBody, fontSize: "13px", fontWeight: 300, color: `${theme.muted}99` }}>
              {lastOrder.delivery === "pickup" ? t("checkout.pickup") : t("checkout.shipping")}
              {lastOrder.delivery === "shipping" && lastOrder.country
                ? ` \u00b7 ${ISO_COUNTRIES.find((c) => c.code === lastOrder.country)?.name ?? lastOrder.country}`
                : ""}
              {lastOrder.address ? ` \u00b7 ${lastOrder.address}` : ""}
            </p>
          </div>
        </div>
      )}

      <button
        onClick={() => navigate("/")}
        className="px-10 py-3 tracking-[0.2em] uppercase transition-all duration-300"
        style={{
          fontFamily: theme.fontBody,
          fontSize: "12px",
          fontWeight: 400,
          backgroundColor: theme.btnBg,
          color: theme.btnText,
          borderRadius: theme.btnRadius,
        }}
      >
        {t("confirmation.continue")}
      </button>
    </div>
  );
}
